import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Users, Plus, Trash2, Loader2, CheckCircle2, Camera, ImagePlus, UserRound, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyAnak = { nama: '', jenis_kelamin: '', usia: '', pendidikan_terakhir: '', pekerjaan: '' };
const emptyKerja = { nama_perusahaan: '', tanggal_mulai: '', tanggal_selesai: '', posisi: '' };

const Field = ({ label, children }) => (
  <div>
    <Label className="text-sm">{label}</Label>
    {children}
  </div>
);

const PublicApplyPage = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    nama: '', nama_panggilan: '', tempat_lahir: '', tanggal_lahir: '', agama: '',
    jenis_kelamin: '', pendidikan: '', status_pernikahan: '', telepon: '', email: '',
    no_ktp: '', no_npwp: '', no_kta: '', tinggi_badan: '', berat_badan: '',
    ukuran_baju: '', ukuran_celana: '', ukuran_sepatu: '', alamat: '',
    nama_pasangan: '', ttl_pasangan: '', agama_pasangan: '', pekerjaan_pasangan: '',
    telepon_pasangan: '', alamat_pasangan: '',
    nama_ayah: '', ttl_ayah: '', agama_ayah: '', pekerjaan_ayah: '',
    nama_ibu: '', ttl_ibu: '', agama_ibu: '', pekerjaan_ibu: '',
    keahlian: '',
  });
  const [anak, setAnak] = useState([]);
  const [pengalamanKerja, setPengalamanKerja] = useState([]);
  const [foto, setFoto] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    return () => streamRef.current?.getTracks().forEach((t) => t.stop());
  }, []);

  const openCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraOpen(true);
    } catch (error) {
      toast.error('Gagal mengakses kamera');
    }
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraOpen(false);
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    setFoto(canvas.toDataURL('image/jpeg', 0.85));
    stopCamera();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setFoto(reader.result);
    reader.readAsDataURL(file);
  };

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const updateAnak = (idx, field, value) => {
    setAnak((prev) => prev.map((item, i) => (i === idx ? { ...item, [field]: value } : item)));
  };
  const updateKerja = (idx, field, value) => {
    setPengalamanKerja((prev) => prev.map((item, i) => (i === idx ? { ...item, [field]: value } : item)));
  };

  const handleSubmit = async () => {
    if (!form.nama.trim() || !form.telepon.trim()) {
      toast.error('Nama lengkap dan nomor telepon wajib diisi');
      return;
    }
    setSubmitting(true);
    try {
      const cleanAnak = anak.filter((a) => a.nama?.trim());
      const cleanKerja = pengalamanKerja.filter((k) => k.nama_perusahaan?.trim());
      await axios.post(`${API_URL}/api/public/applicants`, {
        ...form,
        foto_url: foto,
        anak: cleanAnak,
        pengalaman_kerja_list: cleanKerja,
      });
      setSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengirim formulir, silakan coba lagi');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="apply-success">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-8 space-y-3">
            <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto" />
            <h2 className="text-xl font-bold">Terima Kasih!</h2>
            <p className="text-muted-foreground text-sm">
              Formulir biodata Anda berhasil dikirim. Tim HR akan menghubungi Anda jika ada lowongan yang sesuai.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="apply-page">
      <header className="h-16 bg-white border-b border-border flex items-center px-4 lg:px-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-primary rounded-lg flex items-center justify-center">
            <img src="/logo192.png" alt="SILSA" className="w-full h-full object-cover" />
          </div>
          <span className="text-xl font-bold tracking-tight font-['Manrope']">SILSA</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 lg:p-8 space-y-6">
        <div>
          <h1 className="text-2xl font-bold" data-testid="apply-title">Formulir Biodata Pelamar</h1>
          <p className="text-muted-foreground text-sm mt-1">Silakan isi data diri Anda dengan lengkap dan benar.</p>
        </div>

        <Card data-testid="apply-photo-section">
          <CardHeader><CardTitle className="text-base">Foto Pelamar</CardTitle></CardHeader>
          <CardContent className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
            <div className="w-32 h-32 rounded-lg overflow-hidden bg-slate-100 border flex items-center justify-center shrink-0 relative">
              {foto ? (
                <img src={foto} alt="Foto pelamar" className="w-full h-full object-cover" data-testid="apply-photo-preview" />
              ) : (
                <>
                  <video ref={videoRef} autoPlay playsInline muted className={`w-full h-full object-cover ${cameraOpen ? '' : 'hidden'}`} />
                  {!cameraOpen && <UserRound className="w-10 h-10 text-slate-300" />}
                </>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {!cameraOpen && (
                <>
                  <Button type="button" size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="apply-photo-gallery-btn">
                    <ImagePlus className="w-4 h-4 mr-1" />Pilih dari Galeri
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={openCamera} data-testid="apply-photo-camera-btn">
                    <Camera className="w-4 h-4 mr-1" />Ambil Foto
                  </Button>
                </>
              )}
              {cameraOpen && (
                <Button type="button" size="sm" onClick={capturePhoto} data-testid="apply-photo-capture-btn">
                  <Camera className="w-4 h-4 mr-1" />Jepret
                </Button>
              )}
              {foto && (
                <Button type="button" size="sm" variant="ghost" onClick={() => setFoto(null)} data-testid="apply-photo-remove-btn">
                  <X className="w-4 h-4 mr-1" />Hapus Foto
                </Button>
              )}
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} data-testid="apply-photo-file-input" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Biodata Pribadi</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Nama Lengkap *"><Input data-testid="apply-nama" value={form.nama} onChange={(e) => set('nama', e.target.value)} /></Field>
              <Field label="Nama Panggilan"><Input value={form.nama_panggilan} onChange={(e) => set('nama_panggilan', e.target.value)} /></Field>
              <Field label="Tempat Lahir"><Input data-testid="apply-tempat-lahir" value={form.tempat_lahir} onChange={(e) => set('tempat_lahir', e.target.value)} /></Field>
              <Field label="Tanggal Lahir"><Input data-testid="apply-tanggal-lahir" type="date" value={form.tanggal_lahir} onChange={(e) => set('tanggal_lahir', e.target.value)} /></Field>
              <Field label="Agama"><Input data-testid="apply-agama" value={form.agama} onChange={(e) => set('agama', e.target.value)} /></Field>
              <Field label="Jenis Kelamin">
                <Select value={form.jenis_kelamin} onValueChange={(v) => set('jenis_kelamin', v)}>
                  <SelectTrigger data-testid="apply-jenis-kelamin"><SelectValue placeholder="Pilih" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Laki-laki">Laki-laki</SelectItem>
                    <SelectItem value="Perempuan">Perempuan</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Pendidikan Terakhir"><Input data-testid="apply-pendidikan" value={form.pendidikan} onChange={(e) => set('pendidikan', e.target.value)} /></Field>
              <Field label="Status Pernikahan">
                <Select value={form.status_pernikahan} onValueChange={(v) => set('status_pernikahan', v)}>
                  <SelectTrigger><SelectValue placeholder="Pilih" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Belum Menikah">Belum Menikah</SelectItem>
                    <SelectItem value="Menikah">Menikah</SelectItem>
                    <SelectItem value="Cerai">Cerai</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Nomor Telepon *"><Input data-testid="apply-telepon" value={form.telepon} onChange={(e) => set('telepon', e.target.value)} placeholder="08123456789" /></Field>
              <Field label="Email"><Input data-testid="apply-email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} /></Field>
              <Field label="Nomor KTP"><Input value={form.no_ktp} onChange={(e) => set('no_ktp', e.target.value)} /></Field>
              <Field label="Nomor NPWP"><Input value={form.no_npwp} onChange={(e) => set('no_npwp', e.target.value)} /></Field>
              <Field label="Nomor KTA"><Input value={form.no_kta} onChange={(e) => set('no_kta', e.target.value)} /></Field>
              <Field label="Tinggi Badan (cm)"><Input value={form.tinggi_badan} onChange={(e) => set('tinggi_badan', e.target.value)} /></Field>
              <Field label="Berat Badan (kg)"><Input value={form.berat_badan} onChange={(e) => set('berat_badan', e.target.value)} /></Field>
              <Field label="Ukuran Baju"><Input value={form.ukuran_baju} onChange={(e) => set('ukuran_baju', e.target.value)} /></Field>
              <Field label="Ukuran Celana"><Input value={form.ukuran_celana} onChange={(e) => set('ukuran_celana', e.target.value)} /></Field>
              <Field label="Ukuran Sepatu"><Input value={form.ukuran_sepatu} onChange={(e) => set('ukuran_sepatu', e.target.value)} /></Field>
            </div>
            <Field label="Alamat Tinggal"><Textarea data-testid="apply-alamat" value={form.alamat} onChange={(e) => set('alamat', e.target.value)} rows={2} /></Field>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Status Keluarga - Pasangan</CardTitle><CardDescription>Kosongkan jika belum menikah</CardDescription></CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Nama Istri/Suami"><Input value={form.nama_pasangan} onChange={(e) => set('nama_pasangan', e.target.value)} /></Field>
            <Field label="Tempat/Tanggal Lahir"><Input value={form.ttl_pasangan} onChange={(e) => set('ttl_pasangan', e.target.value)} /></Field>
            <Field label="Agama"><Input value={form.agama_pasangan} onChange={(e) => set('agama_pasangan', e.target.value)} /></Field>
            <Field label="Pekerjaan"><Input value={form.pekerjaan_pasangan} onChange={(e) => set('pekerjaan_pasangan', e.target.value)} /></Field>
            <Field label="Nomor Telepon"><Input value={form.telepon_pasangan} onChange={(e) => set('telepon_pasangan', e.target.value)} /></Field>
            <Field label="Alamat Tinggal"><Input value={form.alamat_pasangan} onChange={(e) => set('alamat_pasangan', e.target.value)} /></Field>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Data Orang Tua</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Nama Ayah Kandung"><Input value={form.nama_ayah} onChange={(e) => set('nama_ayah', e.target.value)} /></Field>
              <Field label="Tempat/Tanggal Lahir Ayah"><Input value={form.ttl_ayah} onChange={(e) => set('ttl_ayah', e.target.value)} /></Field>
              <Field label="Agama Ayah"><Input value={form.agama_ayah} onChange={(e) => set('agama_ayah', e.target.value)} /></Field>
              <Field label="Pekerjaan Ayah"><Input value={form.pekerjaan_ayah} onChange={(e) => set('pekerjaan_ayah', e.target.value)} /></Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Nama Ibu Kandung"><Input value={form.nama_ibu} onChange={(e) => set('nama_ibu', e.target.value)} /></Field>
              <Field label="Tempat/Tanggal Lahir Ibu"><Input value={form.ttl_ibu} onChange={(e) => set('ttl_ibu', e.target.value)} /></Field>
              <Field label="Agama Ibu"><Input value={form.agama_ibu} onChange={(e) => set('agama_ibu', e.target.value)} /></Field>
              <Field label="Pekerjaan Ibu"><Input value={form.pekerjaan_ibu} onChange={(e) => set('pekerjaan_ibu', e.target.value)} /></Field>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="apply-anak-section">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Status Anak</CardTitle>
            <Button size="sm" variant="outline" onClick={() => setAnak((p) => [...p, { ...emptyAnak }])} data-testid="apply-add-anak-btn">
              <Plus className="w-4 h-4 mr-1" />Tambah Anak
            </Button>
          </CardHeader>
          {anak.length > 0 && (
            <CardContent className="space-y-4">
              {anak.map((item, idx) => (
                <div key={idx} className="border rounded-lg p-3 grid grid-cols-1 sm:grid-cols-2 gap-3 relative">
                  <Button size="icon" variant="ghost" className="absolute top-1 right-1 h-6 w-6" onClick={() => setAnak((p) => p.filter((_, i) => i !== idx))}>
                    <Trash2 className="w-3.5 h-3.5 text-destructive" />
                  </Button>
                  <Field label="Nama Anak"><Input data-testid={`apply-anak-nama-${idx}`} value={item.nama} onChange={(e) => updateAnak(idx, 'nama', e.target.value)} /></Field>
                  <Field label="Jenis Kelamin (L/P)"><Input data-testid={`apply-anak-jk-${idx}`} value={item.jenis_kelamin} onChange={(e) => updateAnak(idx, 'jenis_kelamin', e.target.value)} /></Field>
                  <Field label="Usia"><Input data-testid={`apply-anak-usia-${idx}`} value={item.usia} onChange={(e) => updateAnak(idx, 'usia', e.target.value)} /></Field>
                  <Field label="Pendidikan Terakhir"><Input data-testid={`apply-anak-pendidikan-${idx}`} value={item.pendidikan_terakhir} onChange={(e) => updateAnak(idx, 'pendidikan_terakhir', e.target.value)} /></Field>
                  <Field label="Pekerjaan"><Input data-testid={`apply-anak-pekerjaan-${idx}`} value={item.pekerjaan} onChange={(e) => updateAnak(idx, 'pekerjaan', e.target.value)} /></Field>
                </div>
              ))}
            </CardContent>
          )}
        </Card>

        <Card data-testid="apply-kerja-section">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Pengalaman Pekerjaan</CardTitle>
            <Button size="sm" variant="outline" onClick={() => setPengalamanKerja((p) => [...p, { ...emptyKerja }])} data-testid="apply-add-kerja-btn">
              <Plus className="w-4 h-4 mr-1" />Tambah Pengalaman
            </Button>
          </CardHeader>
          {pengalamanKerja.length > 0 && (
            <CardContent className="space-y-4">
              {pengalamanKerja.map((item, idx) => (
                <div key={idx} className="border rounded-lg p-3 grid grid-cols-1 sm:grid-cols-2 gap-3 relative">
                  <Button size="icon" variant="ghost" className="absolute top-1 right-1 h-6 w-6" onClick={() => setPengalamanKerja((p) => p.filter((_, i) => i !== idx))}>
                    <Trash2 className="w-3.5 h-3.5 text-destructive" />
                  </Button>
                  <Field label="Nama Perusahaan"><Input data-testid={`apply-kerja-perusahaan-${idx}`} value={item.nama_perusahaan} onChange={(e) => updateKerja(idx, 'nama_perusahaan', e.target.value)} /></Field>
                  <Field label="Posisi / Jabatan"><Input data-testid={`apply-kerja-posisi-${idx}`} value={item.posisi} onChange={(e) => updateKerja(idx, 'posisi', e.target.value)} /></Field>
                  <Field label="Mulai Bekerja"><Input data-testid={`apply-kerja-mulai-${idx}`} type="date" value={item.tanggal_mulai} onChange={(e) => updateKerja(idx, 'tanggal_mulai', e.target.value)} /></Field>
                  <Field label="Selesai Bekerja"><Input data-testid={`apply-kerja-selesai-${idx}`} type="date" value={item.tanggal_selesai} onChange={(e) => updateKerja(idx, 'tanggal_selesai', e.target.value)} /></Field>
                </div>
              ))}
            </CardContent>
          )}
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Keahlian</CardTitle></CardHeader>
          <CardContent>
            <Textarea placeholder="Contoh: Mengemudi, Komputer, Bela diri, dll." value={form.keahlian} onChange={(e) => set('keahlian', e.target.value)} rows={3} />
          </CardContent>
        </Card>

        <Button className="w-full" size="lg" onClick={handleSubmit} disabled={submitting} data-testid="apply-submit-btn">
          {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Mengirim...</> : 'Kirim Formulir'}
        </Button>
      </main>

      <footer className="p-4 text-center text-xs text-muted-foreground border-t border-border bg-white mt-8">
        &copy; {new Date().getFullYear()} SILSA - PT Lintas Sinergi Andalan
      </footer>
    </div>
  );
};

export default PublicApplyPage;
